import * as React from "react";
import { Routes, Route } from "react-router-dom";

/*
|---------------------------------------------------------------------
|                             Main
|---------------------------------------------------------------------
*/
import AuthLayout from "./layouts/AuthLayout";
import HomeLayout from "./layouts/HomeLayout";
import Dashboard from "./pages/dashboard/Dashboard";
import MainLayout from "./layouts/MainLayout";
import Profile from "./pages/profile/MyProfile";

import "./custom.css";
import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import AllClaims from "./pages/claims/AllClaims";
import AllSafaris from "./pages/safaris/AllSafaris";
import OneClaim from "./pages/claims/OneClaim";
import ActiveUsers from "./pages/manage/users/ActiveUsers";
import AdmClaims from "./pages/claims/AdmClaims";
import AdmClaim from "./pages/claims/AdmClaim";
import Forgot from "./pages/auth/Forgot";
import Reset from "./pages/auth/Reset";
import Denied from "./pages/auth/Denied";
import AllCurrencies from "./pages/manage/currency/AllCurrencies";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import InActiveUsers from "./pages/manage/users/InActiveUsers";
import AllFirms from "./pages/manage/firms/AllFirms";
import UnlinkedUsers from "./pages/manage/users/UnlinkedUsers";
import { useEffect } from "react";
import Signin from "./pages/cauth/Signin";
import Verify from "./pages/cauth/Verify";
const REACT_APP_SESSION_ONEHUB_KEY = process.env.REACT_APP_REACT_APP_SESSION_ONEHUB_KEY || "onehub_access";
const REACT_APP_SESSION_EXPIRY_TIME = process.env.REACT_APP_REACT_APP_SESSION_EXPIRY_TIME || 60 * 1000;

export default function App() {
  const queryClient = new QueryClient();


  useEffect(() => {
    const stored = localStorage.getItem(REACT_APP_SESSION_ONEHUB_KEY);
    const referrer = document.referrer;

    if (stored) {
      const { expiry } = JSON.parse(stored);

      if (Date.now() < expiry) {
        return;
      } else {
        localStorage.removeItem(REACT_APP_SESSION_ONEHUB_KEY);
        window.location.replace("https://karimjee.sharepoint.com/sites/OneHub");
        return;
      }
    }

    if (referrer.includes("karimjee.sharepoint.com")) {
      localStorage.setItem(
        REACT_APP_SESSION_ONEHUB_KEY,
        JSON.stringify({
          expiry: Date.now() + Number(REACT_APP_SESSION_EXPIRY_TIME),
        })
      );
      return;
    }

    window.location.replace("https://karimjee.sharepoint.com/sites/OneHub");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="/" element={<Signin />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/denied" element={<Denied />} />
          </Route>
          <Route element={<HomeLayout />}></Route>
          <Route element={<MainLayout />}>
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/claims"
              element={
                <RequireAuth>
                  <AllClaims />
                </RequireAuth>
              }
            />
            <Route
              path="/safaris"
              element={
                <RequireAuth>
                  <AllSafaris />
                </RequireAuth>
              }
            />
            <Route
              path="/claims/:claim_id/details"
              element={
                <RequireAuth>
                  <OneClaim />
                </RequireAuth>
              }
            />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <Profile />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/users/active"
              element={
                <RequireAuth>
                  <ActiveUsers />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/users/inactive"
              element={
                <RequireAuth>
                  <InActiveUsers />
                </RequireAuth>
              }
            />

            <Route
              path="/manage/users/unlinked"
              element={
                <RequireAuth>
                  <UnlinkedUsers />
                </RequireAuth>
              }
            />

            <Route
              path="/manage/companies"
              element={
                <RequireAuth>
                  <AllFirms />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/claims"
              element={
                <RequireAuth>
                  <AdmClaims />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/currencies"
              element={
                <RequireAuth>
                  <AllCurrencies />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/claims/:claim_id/details"
              element={
                <RequireAuth>
                  <AdmClaim />
                </RequireAuth>
              }
            />
          </Route>
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { auth, signout } = React.useContext(AuthContext);
  if (!auth) {
    signout();
  }
  return children;
}
